/**
 * Sentry integration commands: auth, connect, projects, issues, fix, fix-all, webhook.
 *
 * All commands call backend /api/sentry/* endpoints via the authenticated API client.
 * Sentry credentials stay server-side — the CLI never touches the Sentry API directly.
 *
 * Usage:
 *   spacecode sentry auth --org <slug> --token <token>   # Manual token auth
 *   spacecode sentry auth                                 # OAuth (opens browser)
 *   spacecode sentry auth --remove                        # Remove credentials
 *   spacecode sentry connect -R <repo>                    # Auto-detect project + webhook
 *   spacecode sentry projects
 *   spacecode sentry issues [project] [--limit N]
 *   spacecode sentry fix <issue-id> -R <repo> [-b branch]
 *   spacecode sentry fix-all -R <repo> --project <slug> [--limit N]
 *   spacecode sentry webhook add --project <slug> -R <repo>
 *   spacecode sentry webhook list
 *   spacecode sentry webhook remove <id>
 */

import type { Command } from 'commander';
import chalk from 'chalk';
import { requireAuth } from './auth.js';

export function registerSentryCommand(program: Command): void {
  const sentry = program
    .command('sentry')
    .description('Sentry integration — auto-fix bugs from Sentry issues');

  sentry
    .command('auth')
    .description('Authenticate with Sentry (token or OAuth)')
    .option('--org <slug>', 'Sentry organization slug')
    .option('--token <token>', 'Sentry auth token (with project:read, project:write, event:read scopes)')
    .option('--remove', 'Remove stored Sentry credentials')
    .action(async (options: { org?: string; token?: string; remove?: boolean }) => {
      try {
        const client = await requireAuth();

        if (options.remove) {
          await client.deleteSentryCredentials();
          console.log(chalk.green('Sentry credentials removed.'));
          return;
        }

        if (options.token && options.org) {
          // Manual token auth
          await client.setSentryCredentials(options.token, options.org);
          const { projects } = await client.listSentryProjects();
          console.log(chalk.green(`\nAuthenticated with Sentry org "${options.org}" (${projects.length} projects found)`));
          return;
        }

        if (options.token || options.org) {
          console.error(chalk.red('Both --org and --token are required for manual auth.'));
          process.exit(1);
        }

        // OAuth flow
        console.log(chalk.cyan('Starting Sentry OAuth flow...'));
        const { url, state } = await client.startSentryOAuth();

        const open = (await import('open')).default;
        await open(url);
        console.log(chalk.gray(`\n  Opened browser for Sentry authorization.`));
        console.log(chalk.gray(`  Waiting for authorization...\n`));

        // Poll for completion
        const maxAttempts = 60;
        for (let i = 0; i < maxAttempts; i++) {
          await new Promise((r) => setTimeout(r, 2000));
          const status = await client.pollSentryOAuth(state);
          if (status.completed) {
            console.log(chalk.green(`\nAuthenticated with Sentry org "${status.orgSlug}"`));
            return;
          }
          if (status.error) {
            console.error(chalk.red(`\nOAuth failed: ${status.error}`));
            process.exit(1);
          }
        }

        console.error(chalk.red('\nOAuth timed out. Please try again.'));
        process.exit(1);
      } catch (error) {
        console.error(chalk.red(`Failed: ${error instanceof Error ? error.message : String(error)}`));
        process.exit(1);
      }
    });

  sentry
    .command('connect')
    .description('Connect a Sentry project to a GitHub repo with auto-fix')
    .requiredOption('-R, --repo <repo>', 'GitHub repository (owner/repo)')
    .option('--project <slug>', 'Sentry project slug (auto-detected if only one)')
    .action(async (options: { repo: string; project?: string }) => {
      try {
        const client = await requireAuth();
        const result = await client.sentryConnect(options.repo, options.project) as any;

        if (result.needsSelection) {
          // Multiple projects — interactive picker
          const { select } = await import('@inquirer/prompts');
          const projectSlug = await select<string>({
            message: 'Which Sentry project maps to this repo?',
            choices: result.projects.map((p: any) => ({
              name: `${p.slug}${p.platform ? ` [${p.platform}]` : ''} — ${p.name}`,
              value: p.slug as string,
            })),
          });

          const retryResult = await client.sentryConnect(options.repo, projectSlug) as any;
          printConnectResult(retryResult);
        } else {
          printConnectResult(result);
        }
      } catch (error) {
        console.error(chalk.red(`Failed to connect: ${error instanceof Error ? error.message : String(error)}`));
        process.exit(1);
      }
    });

  sentry
    .command('projects')
    .description('List Sentry projects in your organization')
    .action(async () => {
      try {
        const client = await requireAuth();
        const { projects } = await client.listSentryProjects();

        if (projects.length === 0) {
          console.log(chalk.gray('No projects found in your Sentry organization.'));
          return;
        }

        console.log(chalk.bold(`\n  Sentry Projects (${projects.length}):\n`));
        for (const p of projects) {
          const platform = p.platform ? chalk.gray(` [${p.platform}]`) : '';
          console.log(`  ${chalk.cyan(p.slug)}${platform}  ${p.name}`);
        }
        console.log();
      } catch (error) {
        console.error(chalk.red(`Failed to list projects: ${error instanceof Error ? error.message : String(error)}`));
        process.exit(1);
      }
    });

  sentry
    .command('issues')
    .description('List unresolved Sentry issues')
    .argument('[project]', 'Project slug to filter issues')
    .option('--limit <n>', 'Max issues to return', '25')
    .action(async (project: string | undefined, options: { limit: string }) => {
      try {
        const client = await requireAuth();
        const limit = parseInt(options.limit, 10) || 25;
        const { issues } = await client.listSentryIssues(project, limit);

        if (issues.length === 0) {
          console.log(chalk.gray('No unresolved issues found.'));
          return;
        }

        console.log(chalk.bold(`\n  Unresolved Issues (${issues.length}):\n`));
        for (const i of issues as any[]) {
          const levelColor = i.level === 'error' ? chalk.red : i.level === 'warning' ? chalk.yellow : chalk.gray;
          console.log(`  ${chalk.cyan(i.shortId)} ${levelColor(`[${i.level}]`)} ${i.title}`);
          console.log(`    ${chalk.gray(`${i.count} events  |  last seen: ${i.lastSeen}`)}`);
          if (i.culprit) console.log(`    ${chalk.gray(i.culprit)}`);
          console.log();
        }
      } catch (error) {
        console.error(chalk.red(`Failed to list issues: ${error instanceof Error ? error.message : String(error)}`));
        process.exit(1);
      }
    });

  sentry
    .command('fix')
    .description('Fix a single Sentry issue using a cloud sandbox')
    .argument('<issue-id>', 'Sentry issue ID or short ID')
    .requiredOption('-R, --repo <repo>', 'GitHub repository (owner/repo or URL)')
    .option('-b, --branch <branch>', 'Branch name for the fix')
    .action(async (issueId: string, options: { repo: string; branch?: string }) => {
      try {
        const client = await requireAuth();
        console.log(chalk.cyan(`Dispatching fix for issue ${issueId}...`));

        const result = await client.fixSentryIssue(issueId, options.repo, {
          branch: options.branch,
        });

        if ((result as any).success) {
          console.log(chalk.green(`\nFix dispatched successfully!`));
          if ((result as any).taskId) console.log(`  Task ID: ${(result as any).taskId}`);
          if ((result as any).diff) {
            console.log(chalk.bold('\n--- Diff ---'));
            console.log((result as any).diff);
          }
        } else {
          console.error(chalk.red(`Fix failed: ${(result as any).error ?? 'Unknown error'}`));
          process.exit(1);
        }
      } catch (error) {
        console.error(chalk.red(`Failed to fix issue: ${error instanceof Error ? error.message : String(error)}`));
        process.exit(1);
      }
    });

  sentry
    .command('fix-all')
    .description('Batch fix unresolved issues for a project')
    .requiredOption('-R, --repo <repo>', 'GitHub repository (owner/repo or URL)')
    .requiredOption('--project <slug>', 'Sentry project slug')
    .option('--limit <n>', 'Max issues to fix', '10')
    .action(async (options: { repo: string; project: string; limit: string }) => {
      try {
        const client = await requireAuth();
        const limit = parseInt(options.limit, 10) || 10;

        // Fetch issues first
        const { issues } = await client.listSentryIssues(options.project, limit);

        if (issues.length === 0) {
          console.log(chalk.gray('No unresolved issues found.'));
          return;
        }

        console.log(chalk.bold(`\nFound ${issues.length} issues. Dispatching fixes...\n`));
        for (const i of issues as any[]) {
          console.log(`  ${chalk.cyan(i.shortId)} ${i.title}`);
        }

        const issueIds = (issues as any[]).map((i) => i.id);
        const { results } = await client.fixSentryBatch(issueIds, options.repo);

        console.log(chalk.bold(`\nBatch complete:\n`));
        for (const r of results as any[]) {
          const icon = r.status === 'dispatched' ? chalk.green('OK') : chalk.red('FAIL');
          console.log(`  [${icon}] ${r.issueId}  ${r.branch ?? ''}`);
          if (r.error) console.log(chalk.red(`       ${r.error}`));
        }
      } catch (error) {
        console.error(chalk.red(`Failed to batch fix: ${error instanceof Error ? error.message : String(error)}`));
        process.exit(1);
      }
    });

  // -------------------------------------------------------------------------
  // Webhook auto-fix subcommands
  // -------------------------------------------------------------------------

  const webhook = sentry
    .command('webhook')
    .description('Manage Sentry webhook auto-fix configs');

  webhook
    .command('add')
    .description('Create a webhook config that auto-fixes new Sentry issues')
    .requiredOption('--project <slug>', 'Sentry project slug')
    .requiredOption('-R, --repo <repo>', 'GitHub repository (owner/repo)')
    .action(async (options: { project: string; repo: string }) => {
      try {
        const client = await requireAuth();
        const result = await client.createSentryWebhook(options.project, options.repo) as any;

        console.log(chalk.green('\nWebhook config created!'));

        if (result.webhook.autoRegistered) {
          console.log(chalk.green('\n  Auto-registered in Sentry — no manual setup needed.'));
          console.log(`  ${chalk.bold('Webhook URL:')} ${chalk.cyan(result.webhook.webhookUrl)}`);
        } else {
          console.log(`\n  ${chalk.bold('Webhook URL:')} ${chalk.cyan(result.webhook.webhookUrl)}`);
          console.log(`  ${chalk.bold('Secret:')}      ${result.webhookSecret}`);
          console.log(chalk.gray(`\n  Configure this URL and secret in your Sentry project's webhook settings.`));
          console.log(chalk.gray(`  Events to enable: issue.created, issue.regression`));
          console.log(chalk.gray(`  Tip: add project:write scope to your Sentry token to auto-register.\n`));
        }
      } catch (error) {
        console.error(chalk.red(`Failed to create webhook: ${error instanceof Error ? error.message : String(error)}`));
        process.exit(1);
      }
    });

  webhook
    .command('list')
    .description('List configured Sentry webhooks')
    .action(async () => {
      try {
        const client = await requireAuth();
        const { webhooks } = await client.listSentryWebhooks();

        if (webhooks.length === 0) {
          console.log(chalk.gray('No webhook configs found. Use `spacecode sentry webhook add` to create one.'));
          return;
        }

        console.log(chalk.bold(`\n  Sentry Webhooks (${webhooks.length}):\n`));
        for (const w of webhooks as any[]) {
          const status = w.autoFixEnabled ? chalk.green('active') : chalk.gray('paused');
          const reg = w.autoRegistered ? chalk.cyan('auto') : chalk.yellow('manual');
          console.log(`  ${chalk.cyan(w.id.slice(0, 8))}  ${status}  [${reg}]  ${w.sentryProjectSlug} → ${w.githubRepo}`);
          console.log(`    ${chalk.gray(w.webhookUrl)}`);
          console.log();
        }
      } catch (error) {
        console.error(chalk.red(`Failed to list webhooks: ${error instanceof Error ? error.message : String(error)}`));
        process.exit(1);
      }
    });

  webhook
    .command('remove')
    .description('Remove a webhook config')
    .argument('<id>', 'Webhook config ID')
    .action(async (id: string) => {
      try {
        const client = await requireAuth();
        await client.deleteSentryWebhook(id);
        console.log(chalk.green(`Webhook ${id.slice(0, 8)} removed.`));
      } catch (error) {
        console.error(chalk.red(`Failed to remove webhook: ${error instanceof Error ? error.message : String(error)}`));
        process.exit(1);
      }
    });

  webhook
    .command('pause')
    .description('Pause auto-fix for a webhook')
    .argument('<id>', 'Webhook config ID')
    .action(async (id: string) => {
      try {
        const client = await requireAuth();
        await client.toggleSentryWebhook(id, false);
        console.log(chalk.yellow(`Webhook ${id.slice(0, 8)} paused.`));
      } catch (error) {
        console.error(chalk.red(`Failed to pause: ${error instanceof Error ? error.message : String(error)}`));
        process.exit(1);
      }
    });

  webhook
    .command('resume')
    .description('Resume auto-fix for a webhook')
    .argument('<id>', 'Webhook config ID')
    .action(async (id: string) => {
      try {
        const client = await requireAuth();
        await client.toggleSentryWebhook(id, true);
        console.log(chalk.green(`Webhook ${id.slice(0, 8)} resumed.`));
      } catch (error) {
        console.error(chalk.red(`Failed to resume: ${error instanceof Error ? error.message : String(error)}`));
        process.exit(1);
      }
    });

  webhook
    .command('deliveries')
    .description('List recent deliveries for a webhook')
    .argument('<id>', 'Webhook config ID')
    .option('--limit <n>', 'Max deliveries to return', '20')
    .action(async (id: string, options: { limit: string }) => {
      try {
        const client = await requireAuth();
        const limit = parseInt(options.limit, 10) || 20;
        const { deliveries } = await client.getSentryWebhookDeliveries(id, limit);

        if (deliveries.length === 0) {
          console.log(chalk.gray('No deliveries yet.'));
          return;
        }

        console.log(chalk.bold(`\n  Deliveries (${deliveries.length}):\n`));
        for (const d of deliveries as any[]) {
          console.log(`  ${chalk.gray(d.created_at)}  ${d.action}  issue:${chalk.cyan(d.issue_id)}  [${d.status}]`);
        }
        console.log();
      } catch (error) {
        console.error(chalk.red(`Failed to list deliveries: ${error instanceof Error ? error.message : String(error)}`));
        process.exit(1);
      }
    });
}

function printConnectResult(result: any): void {
  if (result.alreadyExists) {
    console.log(chalk.green(`\nAlready connected: ${result.project.slug} → ${result.webhook.githubRepo}`));
    return;
  }

  const project = result.project?.slug ?? result.webhook?.sentryProjectSlug ?? 'unknown';
  const repo = result.webhook?.githubRepo ?? 'unknown';

  console.log(chalk.green(`\nConnected: ${project} → ${repo}`));

  if (result.webhook?.autoRegistered) {
    console.log(chalk.green('  Auto-registered in Sentry — auto-fix is live.'));
  } else if (result.webhookSecret) {
    console.log(`\n  ${chalk.bold('Webhook URL:')} ${chalk.cyan(result.webhook.webhookUrl)}`);
    console.log(`  ${chalk.bold('Secret:')}      ${result.webhookSecret}`);
    console.log(chalk.gray(`\n  Configure in Sentry project settings to enable auto-fix.`));
    console.log(chalk.gray(`  Tip: add project:write scope to auto-register.\n`));
  }
}
